import { useState, useEffect, useCallback, useMemo } from 'react'
import { useFinances } from 'hooks/useFinances'
import { selectData } from 'layouts/onboarding'
import { BalanceSheetRow } from 'layouts/authenticated/pages/dashboard/components/BalanceSheet/BalanceSheetRow'
import { BalanceSheetModalForm } from 'layouts/authenticated/pages/dashboard/components/BalanceSheetModalForm'
import { DeductionTypeSelection } from './DeductionTypeSelection'
import { useIntake } from 'hooks/useIntakeContext'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import styled from 'styled-components'
import Grid from '@mui/material/Grid'
import { Typography } from '@mui/material'

const BalanceSheetToStyle = ({
  className,
  deductions,
  handleOpen,
  handleViewAsset,
}) => {
  return (
    <Grid className={className}>
      <BalanceSheetRow
        accounts={deductions}
        handleAddAccount={handleOpen}
        handleViewAccount={handleViewAsset}
        category={'deduction'}
        accountType={'Deductions'}
        addAccountButtonText={`+ Add Deduction`}
        isDefaultExpanded
      />
    </Grid>
  )
}

const StyledDeductionsTable = styled(BalanceSheetToStyle)`
  .MuiAccordionSummary-root {
    padding: 0px;
    border-bottom: none;
  }
  .MuiAccordionSummary-root > .MuiAccordionSummary-content {
    margin: 0px;
  }
  .MuiAccordionSummary-root p {
    font-size: 24px !important;
    font-weight: 500;
  }
  .MuiAccordionDetails-root {
    background-color: #fff;
    padding-bottom: 0px;
    border-bottom: none;
  }
  .MuiAccordionDetails-root > div {
    border: 1px solid #ddd;
    border-radius: 8px;
  }
  .MuiAccordionDetails-root > div > div:last-of-type {
    border-bottom: none;
  }
  .MuiAccordionDetails-root > button {
    text-transform: capitalize;
    font-size: 16px;
    font-weight: normal;
    border: 1px solid rgb(221, 221, 221);
    border-radius: 8px;
    width: 100%;
    padding: 16px;
    margin-top: 20px;
  }
`

export const DeductionsStep = () => {
  const { deductionTypes } = selectData
  const { getAccounts, createOrUpdateAccount, deleteAccount } = useFinances()
  const { nonUserState, setNonUserState } = useIntake()
  const [deductions, setDeductions] = useState()
  const [open, setOpen] = useState(false)
  const type = 'deduction'
  const [name, setName] = useState()
  const [subType, setSubType] = useState()
  const [sortKey, setSortKey] = useState()
  const [keepInRetirement, setKeepInRetirement] = useState()
  const [amount, setAmount] = useState()
  const deductionTypesToInput = useMemo(
    () => nonUserState?.deductionTypesToInput || [],
    [nonUserState?.deductionTypesToInput]
  )
  const [selectedDeductions, setSelectedDeductions] = useState(
    (deductionTypesToInput || []).map((deductionType) => ({
      subType: deductionType,
      amount: 0,
      type: 'deduction',
    }))
  )
  const showDeductionForm = (deductionTypesToInput || 0)?.length > 0
  const storeDeductions = useCallback(async () => {
    const accounts = await getAccounts()
    setDeductions((accounts || []).filter(({ type }) => type === 'deduction'))
    setSelectedDeductions(
      (deductionTypesToInput || []).map((deductionType) => ({
        subType: deductionType,
        amount: 0,
        type: 'deduction',
      }))
    )
  }, [getAccounts, deductionTypesToInput])

  function updateSelectedDeductions(subType, amount) {
    const deductionIndex = selectedDeductions.findIndex(
      (deduction) => deduction['subType'] === subType
    )
    if (deductionIndex !== -1) {
      selectedDeductions[deductionIndex] = {
        ...selectedDeductions[deductionIndex],
        amount: amount,
      }
      setSelectedDeductions([...selectedDeductions])
    }
    setNonUserState((prevState) => ({
      ...prevState,
      deductionTypesToInput: selectedDeductions.map(({ subType }) => subType),
      deductionsToSubmit: selectedDeductions,
    }))
  }

  // useEffect(() => {
  //   let loaded = false
  //   if (loaded) return

  //   storeDeductions()
  //   loaded = true
  // }, [getAccounts, storeDeductions])

  const handleClose = useCallback(() => {
    setOpen(false)
    setName('')
    setSubType('')
    setAmount('')
    setSortKey('')
  }, [])

  const handleSubmit = useCallback(async () => {
    handleClose()
  }, [handleClose])

  const handleOpen = useCallback(() => {
    setOpen(true)
  }, [])

  const handleViewAsset = useCallback(
    ({ typeSubTypeUuid, amount, subType }) => {
      setSortKey(typeSubTypeUuid)
      setSubType(subType)
      setAmount(amount)
      setOpen(true)
    },
    []
  )

  return (
    <>
      <BalanceSheetModalForm
        handleClose={handleClose}
        name={name}
        amount={amount}
        subType={subType}
        type={type}
        setSubType={setSubType}
        setName={setName}
        setAmount={setAmount}
        typeSubTypeUuid={sortKey}
        open={open}
        handleSubmit={handleSubmit}
        reloadAccounts={storeDeductions}
        subTypeOptions={deductionTypes}
        // keepInRetirement={keepInRetirement}
        // setKeepInRetirement={setKeepInRetirement}
        modalHeader={'Add/Edit a Deduction'}
        hideName
      />
      {/* <BalanceSheetRow
        accounts={deductions}
        handleAddAccount={handleOpen}
        handleViewAccount={handleViewAsset}
        category={'deduction'}
        accountType={'Deductions'}
        addAccountButtonText={`Add a Deduction`}
        isDefaultExpanded
      /> */}

      {/* replace with the styled version */}
      {/* <StyledDeductionsTable /> */}
      <Grid container spacing={1} mb={2}>
        {(deductionTypesToInput || []).length ? (
          deductionTypesToInput.map((deductionType) => {
            return (
              <Grid
                item
                xs={12}
                md={6}
                xl={8}
                sx={{ mr: { md: '50%', xl: '40%' } }}
                mt={1}
                key={deductionType}
              >
                {renderMuiFormField({
                  autoFocus: false,
                  id: '',
                  label: deductionTypes.find(
                    ({ value }) => value === deductionType
                  )?.label,
                  placeholder: '$112.54',
                  inputType: 'currencyFloat',
                  value: '',
                  shrink: true,
                  onChange: (amount) =>
                    updateSelectedDeductions(deductionType, amount),
                })}
              </Grid>
            )
          })
        ) : (
          <Grid item xs={12}>
            <Typography variant="h4" fontWeight="medium">
              No Deductions Selected
            </Typography>
          </Grid>
        )}
      </Grid>
    </>
  )
}
